import { TimelineLite } from "gsap";
import * as HLF from "html-living-framework";
import { makeSpine } from "html-living-framework/lib/Utils/PixiUtils";
import { floatBetween } from "html-living-framework/lib/Utils/Random";
import {Ease} from "../core/Animatable";
import {IPoint, PointUtil} from "../core/Geom";
import {Callback} from "../core/Reflection";
import {Sprite} from "../core/Sprite";
import * as VO from "../data/VO";
import {AbstractCharacter} from "./AbstractCharacter";
import {TrashItem} from "./TrashItem";
import { TrashItemCollect } from "./TrashItemCollect";
import { TrashItemReturn } from "./TrashItemReturn";

export class Oscar extends AbstractCharacter {

    get collectedItems(): TrashItem[] {
        return this._collectedItems;
    }

    protected static hoseBoneInitialPosition: PIXI.IPointData | undefined;

    public readonly spine = this.addChild(makeSpine("oscar-trash-blaster"));
    public readonly loadPoint = {x: 0, y: 0};

    public minY: number = 0;
    protected slimyclickCount: number = 0;
    protected tracks = {
        FEET: 0,
        ARMS: 1,
        HEAD: 2,
        MOUTH: 3,
        EYES: 4,
        NOZZLE: 5,
        MOTOR: 6,
        HOSE: 7,
        BAG: 8,
        SLIMY_BODY: 9,
        SLIMY_EYES: 10,
    };

    protected readonly animations = {
        BLINK: "blink",
        IDLE: "idle",
        LIP_FLAP: "mouth-flap",
        WALK: "walk",
        WAVE: "",
        SMILE: "easter-egg-reaction",
        SLIMY_BLINK: "slimy-blink",
        SLIMY_WIGGLE: "slimy-wiggle",
    };

    protected readonly _collectedItems: TrashItemReturn[] = [];
    protected slimyClickTarget: PIXI.Container;
    protected nozzleClickTarget: PIXI.Container;
    protected slimyBlinkTimeline = new TimelineLite();

    constructor() {
        super("oscar");
        const ik = this.spine.skeleton.findIkConstraint("right-arm");
        ik.stretch = false;
        const hoseBone = this.spine.skeleton.findBone("right-arm-hose-target");
        if (Oscar.hoseBoneInitialPosition === undefined) {
            Oscar.hoseBoneInitialPosition = {
                x: hoseBone.data.x,
                y: hoseBone.data.y,
            };
        }
        else {
            hoseBone.data.x = Oscar.hoseBoneInitialPosition.x;
            hoseBone.data.y = Oscar.hoseBoneInitialPosition.y;
        }
        this.spine.update(0);

        const slimyClickTarget = this.addChild(new PIXI.Container());
        slimyClickTarget.hitArea = new PIXI.Circle(0, 0, 100);
        slimyClickTarget.addListener(HLF.Input.Events.POINTER_TAP, this.onSlimyClicked.bind(this));

        this.slimyClickTarget = slimyClickTarget;

        const nozzleClickTarget = this.addChild(new PIXI.Container());
        nozzleClickTarget.hitArea = new PIXI.Circle(0, 0, 150);
        nozzleClickTarget.addListener(HLF.Input.Events.POINTER_TAP, this.onNozzleClicked.bind(this));

        this.nozzleClickTarget = nozzleClickTarget;

        this.slimyBlinkTimeline.call(() => {
            this.spine.state.setAnimation(this.tracks.SLIMY_EYES, this.animations.SLIMY_BLINK, false);
            this.slimyBlinkTimeline.delay(floatBetween(1, 5));
            this.slimyBlinkTimeline.restart(true);
        });
        this.slimyBlinkTimeline.delay(floatBetween(1, 5));
    }

    public onLayout() {
        super.onLayout();
    }

    public update(pDeltaTime: number) {
        super.update(pDeltaTime);
        const slimyBone = this.spine.skeleton.findBone("slimy");
        this.slimyClickTarget?.position.copyFrom({x: slimyBone.worldX, y: slimyBone.worldY});
        const nozzleBone = this.spine.skeleton.findBone("nozzle");
        this.nozzleClickTarget?.position.copyFrom({x: nozzleBone.worldX, y: nozzleBone.worldY});
    }

    public destroy() {
        super.destroy();
        this.blinkTimeline.kill();
        this.slimyBlinkTimeline.kill();
    }

    public Smile() {
        if (this.spine.state.getCurrent(this.tracks.HEAD)?.animation?.name !== this.animations.SMILE) {
            this.spine.state.setAnimation(this.tracks.HEAD, this.animations.SMILE, false);
            this.spine.state.addEmptyAnimation(this.tracks.HEAD, 0, 0);
        }
    }

    protected onSlimyClicked(e: PIXI.InteractionEvent) {
        console.log("slimy clicked", e);
        const track = HLF.Application.instance.audio.getAudioTrack("slimey", HLF.AudioCategory.SFX) ?? HLF.Application.instance.audio.createAudioTrack("slimey", HLF.AudioCategory.SFX);
        if (!track.isPlaying()) {
            track.play();
        }
        if (this.slimyclickCount % 2 === 0) {
            HLF.Application.instance.voiceover.playVO(VO.TB_COLLECT02, HLF.PlayMode.New, (pDidPlay: boolean) => {
                if (pDidPlay) {
                    this.slimyclickCount++;
                }
            });
        }
        else {
            this.slimyclickCount++;
        }
        this.spine.state.setAnimation(this.tracks.SLIMY_BODY, this.animations.SLIMY_WIGGLE, false);
        e.stopped = true; // bit of a hack, this still bubbles
    }

    protected onNozzleClicked(e: PIXI.InteractionEvent) {
        console.log("nozzle clicked", e);
        PubSub.publishSync("nozzle clicked", e);
        e.stopPropagation();
    }
}
